<template>
  <Transition name="desktop-page-backdrop">
    <div
      v-if="activeDesktopMenu"
      class="desktop-page-backdrop"
      :class="isLightHeader ? 'desktop-page-backdrop-light' : 'desktop-page-backdrop-dark'"
      aria-hidden="true"
      @click="closeDesktopMenu"
    />
  </Transition>

  <header class="site-header fixed top-0 w-full z-50" :class="{ 'header-tucked': headerTucked }">
    <div
      class="header-surface relative"
      :class="[
        { 'bar-sticky': isScrolling },
        isLightHeader
          ? 'bg-white/95 text-slate-950 shadow-sm shadow-slate-950/5 backdrop-blur-xl dark:bg-slate-950 dark:text-white dark:shadow-none'
          : 'bg-slate-950 dark:bg-slate-950',
      ]"
    >
      <div
        class="border-b text-xs"
        :class="
          isLightHeader
            ? 'border-slate-200 bg-white/90 text-slate-600 dark:border-white/10 dark:bg-slate-950/95 dark:text-slate-300'
            : 'border-white/10 bg-slate-950/95 text-slate-300'
        "
      >
        <!-- Founding 100 offer (data/founding-offer.ts). The same 36px strip, so the header and
             every page offset stay as they are; after the deadline the client only hides it.
             The whole line is the link: on phones there is no room for a separate button. -->
        <div
          v-if="foundingOffer.enabled"
          data-founding-offer="hold"
          class="mx-auto flex h-9 max-w-6xl items-center justify-center px-4 sm:px-6"
          :class="{ invisible: foundingOffer.expired.value }"
          :aria-hidden="foundingOffer.expired.value ? 'true' : undefined"
        >
          <a
            :href="handleReferral(foundingSignupUrl('https://app.workspace369.com/signup'))"
            class="group flex min-w-0 items-center gap-3 self-stretch"
            :tabindex="foundingOffer.expired.value ? -1 : undefined"
            data-conversion-event="start_free_click"
            data-conversion-label="Founding offer banner"
            data-conversion-location="founding-banner"
          >
            <span class="min-w-0 truncate">
              <span class="hidden lg:inline">{{ currentLocaleUi().foundingBannerFull }}</span>
              <span class="lg:hidden">{{ currentLocaleUi().foundingBannerShort }}</span>
            </span>
            <span
              class="hidden shrink-0 font-semibold transition-colors group-hover:underline sm:inline"
              :class="isLightHeader ? 'text-cyan-700 group-hover:text-cyan-900 dark:text-cyan-200' : 'text-cyan-200 group-hover:text-white'"
              >{{ currentLocaleUi().foundingBannerCta }}</span
            >
            <svg
              class="h-3.5 w-3.5 shrink-0 sm:hidden"
              :class="isLightHeader ? 'text-cyan-700 dark:text-cyan-200' : 'text-cyan-200'"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m6 3.5 4.5 4.5L6 12.5" />
            </svg>
          </a>
        </div>
        <!-- Banner links fill the strip's height (self-stretch), so the whole 36px line is the tap target, not the 23px of text. -->
        <!-- Free done-for-you setup (data/setup-offer.ts): the Founding strip's place
             after its deadline, the same 36px line, while the setup offer runs (Dec 31).
             It links to the offer on /pricing (#free-setup), not to sign-up: a trial link needs its
             TrialDisclosure line beside it (B1.1), and a 36px strip has no room for one. The link text
             shows from md: below that, long translations at 125% text would push out the date. -->
        <div
          v-else-if="showSetupStrip"
          data-setup-offer
          class="mx-auto flex h-9 max-w-6xl items-center justify-center px-4 sm:px-6"
        >
          <NuxtLink
            :to="`${localePath('/pricing')}#free-setup`"
            class="group flex min-w-0 items-center gap-3 self-stretch"
            data-conversion-event="pricing_click"
            data-conversion-label="Setup offer banner"
            data-conversion-location="setup-banner"
          >
            <span class="min-w-0 truncate">
              <span class="hidden lg:inline">{{ currentLocaleUi().setupBannerFull }}</span>
              <span class="lg:hidden">{{ currentLocaleUi().setupBannerShort }}</span>
            </span>
            <span
              class="hidden shrink-0 font-semibold transition-colors group-hover:underline md:inline"
              :class="isLightHeader ? 'text-cyan-700 group-hover:text-cyan-900' : 'text-cyan-200 group-hover:text-white'"
              >{{ currentLocaleUi().setupBannerCta }}</span
            >
            <svg
              class="h-3.5 w-3.5 shrink-0 md:hidden"
              :class="isLightHeader ? 'text-cyan-700' : 'text-cyan-200'"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m6 3.5 4.5 4.5L6 12.5" />
            </svg>
          </NuxtLink>
        </div>
        <div v-else class="mx-auto flex h-9 max-w-6xl items-center justify-center gap-2 px-4 sm:px-6">
          <span class="hidden sm:inline">
            {{ currentLocaleUi().topBannerFull }}
          </span>
          <span class="sm:hidden">
            {{ currentLocaleUi().topBannerShort }}
          </span>
          <a
            :href="handleReferral('https://app.workspace369.com')"
            class="flex items-center self-stretch font-semibold transition-colors"
            :class="isLightHeader ? 'text-cyan-700 hover:text-cyan-900 dark:text-cyan-200' : 'text-cyan-200 hover:text-white'"
            data-conversion-event="open_app_click"
            data-conversion-label="Top banner open app"
            data-conversion-location="header-banner"
            >{{ currentLocaleUi().openApp }}</a
          >
        </div>
      </div>
      <div class="header-shell">
        <div class="relative z-30 flex items-center justify-between h-16 md:h-20">
          <!-- Site branding -->
          <div class="desktop-brand self-center sm:ml-0 ml-3 flex-1">
            <!-- Logo -->
            <nuxt-link
              id="logo"
              class="inline-flex items-center sm:mt-2 mt-3"
              :class="isLightHeader ? 'text-slate-950 dark:text-white' : 'text-white'"
              :to="localePath('/')"
              aria-label="Workspace369"
            >
              <Logo
                class="max-w-none h-[38px] w-[38px]"
                :class="isLightHeader ? 'fill-slate-950 dark:fill-white' : 'fill-white'"
              />
              <span
                class="desktop-brand-wordmark ml-2 hidden xl:inline font-semibold"
                :class="isLightHeader ? 'text-slate-950 dark:text-white' : 'text-white'"
                >Workspace369</span
              >
            </nuxt-link>
          </div>

          <!-- Desktop navigation -->
          <nav
            ref="desktopNavigation"
            class="desktop-navigation hidden lg:flex lg:grow"
            aria-label="Primary navigation"
            @mouseenter="cancelDesktopMenuClose"
            @mouseleave="scheduleDesktopMenuClose"
            @focusout="handleDesktopFocusOut"
          >
            <ul class="desktop-navigation-list">
              <li
                v-for="item in desktopNavigationItems()"
                :key="item.id"
                class="desktop-navigation-item"
                @mouseenter="item.groups ? openDesktopMenu(item.id) : closeDesktopMenu()"
              >
                <button
                  v-if="item.groups"
                  :id="`desktop-nav-${item.id}`"
                  class="desktop-navigation-trigger"
                  :class="[
                    desktopNavToneClass,
                    {
                      'desktop-navigation-trigger-open': activeDesktopMenuId === item.id,
                      'desktop-navigation-trigger-active': isDesktopNavActive(item),
                    },
                  ]"
                  type="button"
                  aria-haspopup="true"
                  :aria-controls="`desktop-menu-${item.id}`"
                  :aria-expanded="activeDesktopMenuId === item.id"
                  @click="toggleDesktopMenu(item.id, $event)"
                >
                  <span>{{ item.label }}</span>
                  <svg aria-hidden="true" viewBox="0 0 16 16">
                    <path d="m4.5 6 3.5 3.5L11.5 6" />
                  </svg>
                </button>
                <nuxt-link
                  v-else
                  class="desktop-navigation-trigger desktop-navigation-direct"
                  :class="[
                    desktopNavToneClass,
                    { 'desktop-navigation-trigger-active': isDesktopNavActive(item) },
                  ]"
                  :to="item.to"
                  @focus="closeDesktopMenu"
                >
                  {{ item.label }}
                </nuxt-link>
              </li>
            </ul>
          </nav>

          <!-- Desktop product actions -->
          <ul class="desktop-actions hidden lg:flex justify-end items-center flex-1">
            <li>
              <LanguageSelector :tone="isLightHeader ? 'light' : 'dark'" />
            </li>
            <li>
              <ThemeToggle :tone="isLightHeader ? 'light' : 'dark'" />
            </li>
            <li>
              <a
                class="desktop-action-button desktop-dashboard-button"
                :class="
                  isLightHeader
                    ? 'desktop-dashboard-button-light'
                    : 'desktop-dashboard-button-dark'
                "
                :href="handleReferral('https://app.workspace369.com/login')"
                rel="noopener noreferrer"
                data-conversion-event="sign_in_click"
                data-conversion-label="Header sign in"
                data-conversion-location="header-desktop"
                >{{ currentLocaleUi().signIn }}</a
              >
            </li>
            <li class="desktop-start-item">
              <a
                class="desktop-action-button desktop-start-button"
                :href="handleReferral('https://app.workspace369.com/signup')"
                rel="noopener noreferrer"
                data-conversion-event="start_free_click"
                data-conversion-label="Header start free"
                data-conversion-location="header-desktop"
                aria-describedby="header-trial-terms"
              >
                {{ currentLocaleUi().startFree }}
              </a>
              <!-- Trial terms under the header trial button (legal B1.1), short form: the full line can't fit here. -->
              <TrialDisclosure
                id="header-trial-terms"
                compact
                class="desktop-start-terms"
                :on-light="isLightHeader"
                :on-dark="!isLightHeader"
              />
            </li>
          </ul>

          <!-- Mobile menu -->
          <div class="lg:hidden flex items-center ml-4">
            <LanguageSelector
              class="mr-1"
              mobile
              :tone="isLightHeader ? 'light' : 'dark'"
            />

            <ThemeToggle
              class="mr-1"
              :tone="isLightHeader ? 'light' : 'dark'"
            />

            <!-- Hamburger button -->
            <button
              class="hamburger"
              ref="hamburger"
              :class="{ active: mobileNavOpen }"
              @click.stop="toggleMobileNav"
              aria-controls="mobile-nav"
              :aria-expanded="mobileNavOpen"
              :aria-label="mobileNavOpen ? 'Close navigation menu' : currentLocaleUi().menu"
            >
              <span class="sr-only">{{ currentLocaleUi().menu }}</span>
              <svg
                class="w-6 h-6 fill-current"
                :class="isLightHeader ? 'text-slate-700 dark:text-slate-300' : 'text-slate-300 dark:text-slate-300'"
                viewBox="0 0 24 24"
              >
                <rect class="hamburger-line hamburger-line-top" x="3" y="7" width="18" height="2" rx="1" />
                <rect class="hamburger-line hamburger-line-bottom" x="3" y="15" width="18" height="2" rx="1" />
              </svg>
            </button>

            <!-- Mobile navigation -->
            <Transition name="mobile-nav">
              <nav
                v-if="mobileNavOpen"
                id="mobile-nav"
                class="mobile-nav-panel"
                ref="mobileNav"
                :class="mobileNavToneClass"
                aria-label="Mobile navigation"
                tabindex="-1"
              >
                <div class="mobile-nav-shell">
                  <div class="mobile-nav-direct-list">
                    <nuxt-link
                      v-for="link in mobileDirectLinks()"
                      :key="link.to"
                      class="mobile-nav-direct-link"
                      :class="{ 'mobile-nav-direct-link-active': isActiveMobileLink(link.to) }"
                      :to="link.to"
                      :aria-current="isActiveMobileLink(link.to) ? 'page' : undefined"
                      @click="closeMobileNav"
                    >
                      <span>{{ link.text }}</span>
                      <span aria-hidden="true">→</span>
                    </nuxt-link>
                    <button
                      class="mobile-nav-direct-link mobile-nav-resource-trigger"
                      :class="{
                        'mobile-nav-direct-link-active': isMobileResourceActive,
                        'mobile-nav-resource-trigger-open': mobileResourcesOpen,
                      }"
                      type="button"
                      aria-controls="mobile-resource-links"
                      :aria-expanded="mobileResourcesOpen"
                      @click="toggleMobileResources"
                    >
                      <span>{{ currentHeaderCopy().resources }}</span>
                      <svg aria-hidden="true" viewBox="0 0 20 20">
                        <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
                      </svg>
                    </button>
                    <Transition name="mobile-resource-menu">
                      <div
                        v-if="mobileResourcesOpen"
                        id="mobile-resource-links"
                        class="mobile-nav-resource-menu"
                      >
                        <template v-for="link in mobileResourceLinks" :key="link.to">
                          <a
                            v-if="link.external"
                            class="mobile-nav-resource-link"
                            :href="link.to"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span>{{ link.text }}</span>
                            <span aria-hidden="true">↗</span>
                          </a>
                          <nuxt-link
                            v-else
                            class="mobile-nav-resource-link"
                            :class="{ 'mobile-nav-resource-link-active': isActiveMobileLink(link.to) }"
                            :to="link.to"
                            @click="closeMobileNav"
                          >
                            <span>{{ link.text }}</span>
                            <span aria-hidden="true">→</span>
                          </nuxt-link>
                        </template>
                      </div>
                    </Transition>
                    <a
                      class="mobile-nav-direct-link mobile-nav-direct-signin"
                      :href="handleReferral('https://app.workspace369.com/login')"
                      rel="noopener noreferrer"
                      data-conversion-event="sign_in_click"
                      data-conversion-label="Mobile menu sign in"
                      data-conversion-location="mobile-menu"
                    >
                      <span>{{ currentLocaleUi().signIn }}</span>
                      <span aria-hidden="true">→</span>
                    </a>
                  </div>

                  <div class="mobile-nav-actions">
                    <a
                      class="mobile-nav-cta"
                      :href="handleReferral('https://app.workspace369.com/signup')"
                      rel="noopener noreferrer"
                      data-conversion-event="start_free_click"
                      data-conversion-label="Mobile menu start now"
                      data-conversion-location="mobile-menu"
                    >
                      <span>{{ currentLocaleUi().startFree }}</span>
                      <span aria-hidden="true">→</span>
                    </a>
                    <nuxt-link
                      class="mobile-nav-contact"
                      :to="localePath('/contact-sales')"
                      data-conversion-event="contact_sales_click"
                      data-conversion-label="Mobile menu contact sales"
                      data-conversion-location="mobile-menu"
                      @click="closeMobileNav"
                    >
                      Contact Sales
                    </nuxt-link>
                    <!-- Trial terms right under the trial button row, across both columns (legal B1.1), as on the sticky bars. -->
                    <TrialDisclosure
                      compact
                      class="mobile-nav-terms"
                      :on-light="mobileNavToneClass === 'mobile-nav-panel-light'"
                      :on-dark="mobileNavToneClass !== 'mobile-nav-panel-light'"
                    />
                  </div>
                </div>
              </nav>
            </Transition>
          </div>
        </div>
      </div>

      <Transition name="desktop-mega-menu">
        <div
          v-if="activeDesktopMenu"
          :id="`desktop-menu-${activeDesktopMenu.id}`"
          ref="desktopMegaPanel"
          class="desktop-mega-menu"
          :class="isLightHeader ? 'desktop-mega-menu-light' : 'desktop-mega-menu-dark'"
          :aria-labelledby="`desktop-nav-${activeDesktopMenu.id}`"
          @mouseenter="cancelDesktopMenuClose"
          @mouseleave="scheduleDesktopMenuClose"
          @focusout="handleDesktopFocusOut"
        >
          <div class="desktop-mega-menu-shell">
            <section class="desktop-mega-menu-intro">
              <p>{{ activeDesktopMenu.eyebrow }}</p>
              <h2>{{ activeDesktopMenu.title }}</h2>
              <p>{{ activeDesktopMenu.description }}</p>
              <nuxt-link
                v-if="activeDesktopMenu.overview"
                :to="activeDesktopMenu.overview.to"
                @click="closeDesktopMenu"
              >
                <span>{{ activeDesktopMenu.overview.label }}</span>
                <span aria-hidden="true">→</span>
              </nuxt-link>
            </section>

            <div class="desktop-mega-menu-groups">
              <section
                v-for="group in activeDesktopMenu.groups"
                :key="group.title"
                class="desktop-mega-menu-group"
              >
                <h3>{{ group.title }}</h3>
                <div class="desktop-mega-menu-links">
                  <template v-for="link in group.links" :key="link.to">
                    <a
                      v-if="link.external"
                      :href="link.to"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click="closeDesktopMenu"
                    >
                      <strong>{{ link.label }}</strong>
                      <small>{{ link.description }}</small>
                    </a>
                    <nuxt-link v-else :to="link.to" @click="closeDesktopMenu">
                      <strong>{{ link.label }}</strong>
                      <small>{{ link.description }}</small>
                    </nuxt-link>
                  </template>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </header>
</template>

<script>
import { useReferral } from "../composables/useReferral";
import { getHeaderLocalizedCopy } from "../data/header-localized-copy";
import { localizedPath } from "../data/locales";
import { foundingSignupUrl } from "../data/founding-offer";
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from "vue";

export default {
  name: "Header",
  setup() {
    const { addReferralToUrl } = useReferral();
    const { basePath, copyLocaleCode, currentLocaleCode, localeUi } = useWorkspaceLocale();
    const foundingOffer = useFoundingOffer();
    const showSetupStrip = useSetupStrip();
    const colorMode = useColorMode();
    const route = useRoute();
    const serverLocaleCode = import.meta.server ? currentLocaleCode.value : "";
    const headerLocaleCode = () => serverLocaleCode || currentLocaleCode.value;
    const localePath = (path) => localizedPath(path, headerLocaleCode());
    // Links follow the URL's locale; copy follows the rendered one, which is English
    // on generated editions (their page dictionary translates it).
    const currentHeaderCopy = () => getHeaderLocalizedCopy(copyLocaleCode.value);
    const currentLocaleUi = () => localeUi.value;

    const mobileDirectLinks = () => [
      { text: currentLocaleUi().nav.home, to: localePath("/") },
      { text: currentLocaleUi().nav.solutions, to: localePath("/solutions") },
      { text: currentLocaleUi().nav.features, to: localePath("/features") },
      { text: currentLocaleUi().nav.buildWithAi, to: localePath("/build-a-crm-with-ai") },
      { text: currentLocaleUi().nav.pricing, to: localePath("/pricing") },
    ];

    const mobileResourceLinks = computed(() => [
      { text: "Learning center", to: localePath("/learn") },
      { text: "Research & Statistics", to: localePath("/research") },
      { text: "Blog", to: localePath("/blog") },
      { text: "Documentation", to: "https://docs.workspace369.com", external: true },
      { text: "Community", to: localePath("/community") },
      { text: "Customer Stories", to: localePath("/customers") },
      { text: "Get Support", to: localePath("/support") },
      { text: "Product roadmap", to: localePath("/roadmap") },
    ]);

    const desktopNavigationItems = () => [
      {
        id: "features",
        label: currentLocaleUi().nav.features,
        eyebrow: "Product capabilities",
        title: "One system, connected by design.",
        description: "Move client work from the first request through delivery, communication, billing, and reporting.",
        overview: { label: "Explore every feature", to: localePath("/features") },
        groups: [
          {
            title: "Client operations",
            links: [
              { label: "CRM & client portals", description: "Records, requests, portals, and history", to: localePath("/features/client-portal") },
              { label: "Projects & scheduling", description: "Tasks, calendars, notes, and delivery", to: localePath("/features/schedule") },
              { label: "Files & document history", description: "Versioned files beside the work", to: localePath("/features/files") },
              { label: "Team access controls", description: "Keep assigned client work scoped", to: localePath("/features/client-scoped-access") },
            ],
          },
          {
            title: "Communication",
            links: [
              { label: "Shared Inbox", description: "Email, SMS, and client conversations", to: localePath("/features/communications") },
              { label: "Workspace Voice", description: "Calling, voicemail, routing, and history", to: localePath("/features/workspace-voice") },
              { label: "Auto-attendant & IVR", description: "Route callers with business context", to: localePath("/features/auto-attendant-and-ivr") },
              { label: "Post-call automation", description: "Summaries, tasks, and follow-up", to: localePath("/features/post-call-automation") },
            ],
          },
          {
            title: "Revenue & intelligence",
            links: [
              { label: "Quotes & proposals", description: "Shape the offer and approval path", to: localePath("/features/quoting") },
              { label: "Invoices & payments", description: "Bill, collect, and track status", to: localePath("/features/invoices") },
              { label: "AI & automations", description: "Reduce repetitive operational work", to: localePath("/features/automation") },
              { label: "Reporting & analytics", description: "See performance across the operation", to: localePath("/features/analytics") },
            ],
          },
        ],
      },
      {
        id: "solutions",
        label: currentLocaleUi().nav.solutions,
        eyebrow: "Operational outcomes",
        title: "Start with the work you need to improve.",
        description: "Choose a connected workflow now, then add deeper communication, finance, AI, and controls when needed.",
        overview: { label: "Browse all solutions", to: localePath("/solutions") },
        groups: [
          {
            title: "Connect the operation",
            links: [
              { label: "Client work operating system", description: "Run the full request-to-payment path", to: localePath("/solutions/client-work-operating-system") },
              { label: "Workspace CRM", description: "Keep relationship and delivery context together", to: localePath("/solutions/workspace-crm") },
              { label: "CRM, projects & invoicing", description: "Replace a stitched-together stack", to: localePath("/solutions/workspace-crm") },
              { label: "Client onboarding", description: "Move new clients into delivery cleanly", to: localePath("/solutions/client-onboarding-software") },
            ],
          },
          {
            title: "Communicate with context",
            links: [
              { label: "Business phone with CRM", description: "Calls and client records in one workflow", to: localePath("/solutions/business-phone-system-with-crm") },
              { label: "Shared inbox", description: "Coordinate team and client conversations", to: localePath("/solutions/shared-inbox-software") },
              { label: "SMS inbox", description: "Text, follow up, and retain history", to: localePath("/solutions/sms-inbox-for-client-work") },
              { label: "AI call summaries", description: "Turn conversations into next steps", to: localePath("/solutions/ai-call-summary-software") },
            ],
          },
          {
            title: "Bill and understand",
            links: [
              { label: "Professional services billing", description: "Connect delivery, time, and revenue", to: localePath("/solutions/professional-services-billing-software") },
              { label: "Time tracking & invoicing", description: "Move approved time into billing", to: localePath("/solutions/time-tracking-and-invoicing-software") },
              { label: "Project profitability", description: "Understand revenue, cost, and margin", to: localePath("/solutions/project-profitability-software-for-agencies") },
              { label: "Client portal & billing", description: "Give clients one clear place to act", to: localePath("/solutions/client-portal-and-billing-software") },
            ],
          },
        ],
      },
      {
        id: "resources",
        label: currentHeaderCopy().resources,
        eyebrow: "Learn and get help",
        title: "Practical guidance for better client operations.",
        description: "Research the product, learn from real workflows, and reach the right team without digging through the site.",
        groups: [
          {
            title: "Learn",
            links: [
              { label: "Learning center", description: "Evergreen guides for client work", to: localePath("/learn") },
              { label: "Research & Statistics", description: "Primary-source evidence", to: localePath("/research") },
              { label: "Blog", description: "Buying guides and operating playbooks", to: localePath("/blog") },
              { label: "Documentation", description: "Searchable, versioned product guidance", to: "https://docs.workspace369.com", external: true },
            ],
          },
          {
            title: "Connect",
            links: [
              { label: "Community", description: "Share questions and practical feedback", to: localePath("/community") },
              { label: "Customer stories", description: "See how teams connect their work", to: localePath("/customers") },
            ],
          },
          {
            title: "Get help",
            links: [
              { label: "Get support", description: "Route a request with the right context", to: localePath("/support") },
              { label: "Product roadmap", description: "See what is available and being explored", to: localePath("/roadmap") },
            ],
          },
        ],
      },
      {
        id: "industries",
        label: currentHeaderCopy().industries,
        eyebrow: "Ways of working",
        title: "Built for businesses that deliver client work.",
        description: "Find the workflow that matches how your team sells, communicates, delivers, and gets paid.",
        groups: [
          {
            title: "Agencies",
            links: [
              { label: "Agencies", description: "CRM, delivery, billing, and reporting", to: localePath("/solutions/agency-management-software") },
              { label: "Marketing agencies", description: "Campaign work and client operations", to: localePath("/solutions/marketing-agency-project-management-software") },
              { label: "Creative agencies", description: "Projects, reviews, files, and billing", to: localePath("/solutions/creative-agency-project-management-software") },
              { label: "SEO agencies", description: "Retainers, delivery, and client reporting", to: localePath("/solutions/seo-agency-management-software") },
            ],
          },
          {
            title: "Independent professionals",
            links: [
              { label: "Consultants", description: "Proposals, retainers, invoices, and follow-up", to: localePath("/solutions/invoicing-software-for-consultants") },
              { label: "Freelancers", description: "Track work, expenses, and payments", to: localePath("/solutions/freelancer-invoicing-software") },
            ],
          },
          {
            title: "Client service teams",
            links: [
              { label: "Professional services", description: "Connect expertise, delivery, and revenue", to: localePath("/solutions/professional-services-billing-software") },
              { label: "Service businesses", description: "Manage clients from request to payment", to: localePath("/solutions/service-business-management-software") },
              { label: "Google Workspace teams", description: "Add CRM and operations around existing tools", to: localePath("/solutions/crm-for-google-workspace") },
            ],
          },
        ],
      },
      {
        id: "build-with-ai",
        label: currentLocaleUi().nav.buildWithAi,
        to: localePath("/build-a-crm-with-ai"),
      },
      {
        id: "pricing",
        label: currentLocaleUi().nav.pricing,
        to: localePath("/pricing"),
      },
    ];

    // Path only: basePath keeps the query string, so an ad visit (/?utm_source=…) used to get the
    // dark inner-page header on the home page once it hydrated.
    const isHomePage = computed(() => (basePath.value.split(/[?#]/)[0] || "/") === "/");

    const handleReferral = (baseUrl) => {
      return addReferralToUrl(baseUrl);
    };

    const closeMobileNav = () => {
      mobileNavOpen.value = false;
    };

    const toggleMobileNav = () => {
      mobileNavOpen.value = !mobileNavOpen.value;
    };

    const toggleMobileResources = () => {
      mobileResourcesOpen.value = !mobileResourcesOpen.value;
    };

    const normalizePath = (path) => {
      if (!path || path === "/") return "/";
      return path.endsWith("/") ? path.slice(0, -1) : path;
    };

    const isActiveMobileLink = (to) => {
      const currentPath = normalizePath(route.path);
      const targetPath = normalizePath(to);
      if (targetPath === "/") return currentPath === "/";
      return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    };

    const activeDesktopMenuId = ref("");
    const mobileNavOpen = ref(false);
    const mobileResourcesOpen = ref(false);
    const desktopNavigation = ref(null);
    const desktopMegaPanel = ref(null);
    const lastDesktopMenuTrigger = ref(null);
    const mobileNav = ref(null);
    const hamburger = ref(null);
    const isScrolling = ref(false);
    const isMounted = ref(false);

    const isDarkTheme = computed(() => isMounted.value && colorMode.value === "dark");
    const isLightHeader = computed(() => isHomePage.value && !isDarkTheme.value);
    const mobileNavToneClass = computed(() =>
      isLightHeader.value && !isDarkTheme.value ? "mobile-nav-panel-light" : "mobile-nav-panel-dark",
    );
    const isMobileResourceActive = computed(() =>
      mobileResourceLinks.value.some(
        (link) => !link.external && isActiveMobileLink(link.to),
      ),
    );

    const activeDesktopMenu = computed(() =>
      desktopNavigationItems().find((item) => item.id === activeDesktopMenuId.value),
    );

    const desktopNavToneClass = computed(() =>
      isLightHeader.value ? "desktop-navigation-trigger-light" : "desktop-navigation-trigger-dark",
    );

    const desktopItemPaths = (item) =>
      item.groups?.flatMap((group) => group.links.filter((link) => !link.external).map((link) => normalizePath(link.to))) || [];

    const isDesktopNavActive = (item) => {
      const currentPath = normalizePath(basePath.value);
      if (item.id === "pricing") return currentPath === "/pricing";
      if (item.id === "features") return currentPath === "/features" || currentPath.startsWith("/features/");
      if (item.id === "resources") return desktopItemPaths(item).some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
      if (item.id === "industries") return desktopItemPaths(item).some((path) => currentPath === path);
      if (item.id === "solutions") {
        const industryItem = desktopNavigationItems().find((entry) => entry.id === "industries");
        const isIndustryPath = industryItem && desktopItemPaths(industryItem).some((path) => currentPath === path);
        return !isIndustryPath && (currentPath === "/solutions" || currentPath.startsWith("/solutions/"));
      }
      return false;
    };

    let desktopMenuCloseTimer;

    const cancelDesktopMenuClose = () => {
      if (!desktopMenuCloseTimer) return;
      window.clearTimeout(desktopMenuCloseTimer);
      desktopMenuCloseTimer = undefined;
    };

    const closeDesktopMenu = () => {
      cancelDesktopMenuClose();
      activeDesktopMenuId.value = "";
    };

    const scheduleDesktopMenuClose = () => {
      if (!import.meta.client) return;
      cancelDesktopMenuClose();
      desktopMenuCloseTimer = window.setTimeout(closeDesktopMenu, 140);
    };

    const openDesktopMenu = (menuId, event) => {
      cancelDesktopMenuClose();
      activeDesktopMenuId.value = menuId;
      if (event?.currentTarget) lastDesktopMenuTrigger.value = event.currentTarget;
    };

    const toggleDesktopMenu = (menuId, event) => {
      if (activeDesktopMenuId.value === menuId) {
        cancelDesktopMenuClose();
        if (event?.currentTarget) lastDesktopMenuTrigger.value = event.currentTarget;
        return;
      }
      openDesktopMenu(menuId, event);
    };

    const handleDesktopFocusOut = ({ relatedTarget }) => {
      if (
        desktopNavigation.value?.contains(relatedTarget) ||
        desktopMegaPanel.value?.contains(relatedTarget)
      ) return;
      scheduleDesktopMenuClose();
    };

    // On the home page on phones the 101px header (banner + nav) tucks away while you
    // scroll down and comes back on the first scroll up, so content gets the screen.
    const headerTucked = ref(false);
    const isHomeRoute = computed(() => /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/?)?$/i.test(route.path));
    let lastScrollY = 0;

    const handleScroll = () => {
      const y = window.scrollY;
      isScrolling.value = y > 0;
      if (!isHomeRoute.value || mobileNavOpen.value || window.innerWidth >= 768) {
        headerTucked.value = false;
      } else if (y < 160) {
        headerTucked.value = false;
      } else if (y > lastScrollY + 6) {
        headerTucked.value = true;
      } else if (y < lastScrollY - 6) {
        headerTucked.value = false;
      }
      if (Math.abs(y - lastScrollY) > 6) lastScrollY = y;
    };

    // close on click outside
    const clickHandler = ({ target }) => {
      if (
        activeDesktopMenuId.value &&
        !desktopNavigation.value?.contains(target) &&
        !desktopMegaPanel.value?.contains(target)
      ) closeDesktopMenu();

      if (
        !mobileNavOpen.value ||
        mobileNav.value?.contains(target) ||
        hamburger.value?.contains(target)
      )
        return;
      closeMobileNav();
    };

    // close if the esc key is pressed
    const keyHandler = ({ key }) => {
      if (key !== "Escape") return;
      if (mobileNavOpen.value) {
        closeMobileNav();
        hamburger.value?.focus();
        return;
      }
      if (activeDesktopMenuId.value) {
        closeDesktopMenu();
        lastDesktopMenuTrigger.value?.focus();
      }
    };

    watch(
      () => route.fullPath,
      () => {
        closeMobileNav();
        closeDesktopMenu();
      },
    );

    watch(mobileNavOpen, async (isOpen) => {
      if (!import.meta.client) return;
      document.body.style.overflow = isOpen ? "hidden" : "";
      if (isOpen) {
        mobileResourcesOpen.value = isMobileResourceActive.value;
        await nextTick();
        mobileNav.value?.focus();
      }
    });

    watch(isHomeRoute, () => {
      headerTucked.value = false;
    });

    let desktopHeaderQuery;
    const handleDesktopHeaderChange = ({ matches }) => {
      if (matches) closeMobileNav();
    };

    onMounted(() => {
      isMounted.value = true;
      document.addEventListener("click", clickHandler);
      document.addEventListener("keydown", keyHandler);
      window.addEventListener("scroll", handleScroll, { passive: true });
      if (typeof window.matchMedia === "function") {
        desktopHeaderQuery = window.matchMedia("(min-width: 1024px)");
        desktopHeaderQuery.addEventListener("change", handleDesktopHeaderChange);
      }
    });

    onUnmounted(() => {
      cancelDesktopMenuClose();
      document.body.style.overflow = "";
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("scroll", handleScroll);
      desktopHeaderQuery?.removeEventListener("change", handleDesktopHeaderChange);
    });

    return {
      headerTucked,
      activeDesktopMenuId,
      activeDesktopMenu,
      desktopNavigation,
      desktopMegaPanel,
      desktopNavigationItems,
      desktopNavToneClass,
      isDesktopNavActive,
      openDesktopMenu,
      closeDesktopMenu,
      toggleDesktopMenu,
      scheduleDesktopMenuClose,
      cancelDesktopMenuClose,
      handleDesktopFocusOut,
      mobileNavOpen,
      mobileNav,
      hamburger,
      isScrolling,
      handleReferral,
      closeMobileNav,
      toggleMobileNav,
      toggleMobileResources,
      mobileDirectLinks,
      mobileResourceLinks,
      mobileResourcesOpen,
      isMobileResourceActive,
      isActiveMobileLink,
      mobileNavToneClass,
      isHomePage,
      isLightHeader,
      localePath,
      currentHeaderCopy,
      currentLocaleUi,
      foundingOffer,
      foundingSignupUrl,
      showSetupStrip,
    };
  },
};
</script>

<style scoped>
.site-header {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.site-header.header-tucked {
  transform: translateY(-100%);
}

@media (prefers-reduced-motion: reduce) {
  .site-header {
    transition: none;
  }
}

.bar-sticky::before {
  content: "";
  position: absolute;
  inset: 0px;
  bottom: -20px;
  -webkit-backdrop-filter: blur(0px);
  backdrop-filter: blur(0px);
  -webkit-mask: linear-gradient(to-b, black, transparent);
  mask: linear-gradient(to-b, black, transparent);
  -webkit-mask: linear-gradient(to bottom, black 60px, transparent);
  transform: translate3d(0, 0, 0);
  pointer-events: none;
}

/* Add these new styles for the top banner */
.bg-slate-900 {
  background-color: rgb(15 23 42);
}

/* Tablet and desktop mega navigation */
.desktop-page-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: none;
  cursor: default;
  -webkit-backdrop-filter: blur(4px) saturate(0.94);
  backdrop-filter: blur(4px) saturate(0.94);
}

.desktop-page-backdrop-light {
  background: rgb(15 23 42 / 0.075);
}

.desktop-page-backdrop-dark {
  background: rgb(2 6 23 / 0.17);
}

.desktop-page-backdrop-enter-active,
.desktop-page-backdrop-leave-active {
  transition: opacity 180ms ease;
}

.desktop-page-backdrop-enter-from,
.desktop-page-backdrop-leave-to {
  opacity: 0;
}

.desktop-brand,
.desktop-actions {
  flex: 0 0 auto;
}

.desktop-navigation {
  min-width: 0;
  justify-content: center;
}

.desktop-navigation-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  min-width: 0;
  margin: 0;
  padding: 0 0.35rem;
  list-style: none;
}

.desktop-navigation-item {
  display: flex;
  align-items: center;
}

.desktop-navigation-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-height: 2.5rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  padding: 0 0.58rem;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 150ms ease,
    color 150ms ease;
}

.desktop-navigation-trigger::after {
  content: "";
  position: absolute;
  right: 0.58rem;
  bottom: 0.22rem;
  left: 0.58rem;
  height: 2px;
  background: #1ba7ce;
  opacity: 0;
  transform: scaleX(0.35);
  transition:
    opacity 150ms ease,
    transform 180ms ease;
}

.desktop-navigation-trigger svg {
  width: 0.78rem;
  height: 0.78rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.75;
  transition: transform 160ms ease;
}

.desktop-navigation-trigger-light {
  color: #475569;
}

.desktop-navigation-trigger-dark {
  color: #cbd5e1;
}

.desktop-navigation-trigger-light:hover,
.desktop-navigation-trigger-light:focus-visible,
.desktop-navigation-trigger-light.desktop-navigation-trigger-open {
  background: rgb(15 23 42 / 0.055);
  color: #020617;
}

.desktop-navigation-trigger-dark:hover,
.desktop-navigation-trigger-dark:focus-visible,
.desktop-navigation-trigger-dark.desktop-navigation-trigger-open {
  background: rgb(255 255 255 / 0.07);
  color: #ffffff;
}

.desktop-navigation-trigger-open svg {
  transform: rotate(180deg);
}

.desktop-navigation-trigger-active::after,
.desktop-navigation-trigger-open::after {
  opacity: 1;
  transform: scaleX(1);
}

.desktop-navigation-trigger:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 2px;
}

.desktop-navigation-direct {
  padding-inline: 0.72rem;
}

.desktop-actions > li {
  margin-left: 0.35rem;
}

.desktop-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5.75rem;
  min-height: 2.5rem;
  border: 1px solid transparent;
  border-radius: 0.45rem;
  padding: 0 0.8rem;
  font-size: 0.78rem;
  font-weight: 720;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease,
    transform 150ms ease;
}

.desktop-action-button:hover {
  transform: translateY(-1px);
}

.desktop-action-button:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 2px;
}

.desktop-dashboard-button-light {
  border-color: #0f172a;
  background: #0f172a;
  color: #ffffff;
}

.desktop-dashboard-button-light:hover {
  border-color: #1e293b;
  background: #1e293b;
}

/* The home page server-renders its light header (the theme is only known in the browser), while
   the color-mode script sets html.dark before the first paint; these keep a dark-theme visit dark
   until hydration swaps in the -dark classes. */
:global(.dark .desktop-navigation-trigger-light) {
  color: #cbd5e1;
}

:global(.dark .desktop-dashboard-button-light) {
  border-color: #334155;
  background: #172033;
  color: #f8fafc;
}

.desktop-dashboard-button-dark {
  border-color: #334155;
  background: #172033;
  color: #f8fafc;
}

.desktop-dashboard-button-dark:hover {
  border-color: #475569;
  background: #243146;
}

.desktop-start-button {
  background: #0e7490;
  color: #ffffff;
  box-shadow: 0 8px 20px rgb(27 167 206 / 0.18);
}

.desktop-start-button:hover {
  background: #155e75;
}

/* The terms hang under the button inside the header's own height, so the header doesn't grow (CLS 0).
   TrialDisclosure has two root nodes and never gets this component's scoped attribute: use :deep. */
.desktop-start-item {
  position: relative;
}

.desktop-start-item :deep(.desktop-start-terms) {
  position: absolute;
  top: calc(100% + 2px);
  inset-inline-end: 0;
  margin: 0;
  max-width: none;
  white-space: nowrap;
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: end;
  pointer-events: none;
}

.desktop-mega-menu {
  --mega-page: #ffffff;
  --mega-intro: #f5f8fb;
  --mega-text: #0f172a;
  --mega-muted: #64748b;
  --mega-line: rgb(15 23 42 / 0.11);
  --mega-hover: #edf7fa;
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  z-index: 20;
  max-height: calc(100dvh - 7.25rem);
  overflow: hidden auto;
  border-top: 1px solid var(--mega-line);
  border-bottom: 1px solid var(--mega-line);
  background: var(--mega-page);
  color: var(--mega-text);
  box-shadow: 0 26px 64px rgb(15 23 42 / 0.16);
}

.desktop-mega-menu-dark {
  --mega-page: #050c1b;
  --mega-intro: #09162a;
  --mega-text: #f8fafc;
  --mega-muted: #94a3b8;
  --mega-line: rgb(148 163 184 / 0.2);
  --mega-hover: #0b2035;
  box-shadow: 0 30px 72px rgb(2 6 23 / 0.56);
}

.desktop-mega-menu-shell {
  display: grid;
  grid-template-columns: minmax(13.5rem, 0.8fr) minmax(0, 2.45fr);
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
}

/* Header bar container — same shell formula as the feature-page content
   (min(100% - 3rem, 84rem)) so logo, nav, and page content share edges. */
.header-shell {
  width: min(100% - 3rem, 84rem);
  margin: 0 auto;
}

.desktop-mega-menu-intro {
  min-width: 0;
  border-right: 1px solid var(--mega-line);
  background: var(--mega-intro);
  padding: 1.75rem 1.5rem 1.85rem;
}

.desktop-mega-menu-intro > p:first-child {
  margin: 0 0 0.72rem;
  color: #0e8eb3;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1.3;
  text-transform: uppercase;
}

.desktop-mega-menu-dark .desktop-mega-menu-intro > p:first-child {
  color: #67e8f9;
}

.desktop-mega-menu-intro h2 {
  max-width: 17rem;
  margin: 0;
  color: var(--mega-text);
  font-size: clamp(1.18rem, 2vw, 1.55rem);
  font-weight: 750;
  line-height: 1.18;
}

.desktop-mega-menu-intro > p:nth-child(3) {
  max-width: 18rem;
  margin: 0.85rem 0 0;
  color: var(--mega-muted);
  font-size: 0.76rem;
  line-height: 1.65;
}

.desktop-mega-menu-intro > a {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 1.25rem;
  color: #0e8eb3;
  font-size: 0.75rem;
  font-weight: 750;
  text-decoration: none;
}

.desktop-mega-menu-dark .desktop-mega-menu-intro > a {
  color: #67e8f9;
}

.desktop-mega-menu-intro > a span:last-child {
  transition: transform 150ms ease;
}

.desktop-mega-menu-intro > a:hover span:last-child {
  transform: translateX(0.18rem);
}

.desktop-mega-menu-groups {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-width: 0;
  padding: 1.75rem 1.35rem 1.55rem;
}

.desktop-mega-menu-group {
  min-width: 0;
  border-right: 1px solid var(--mega-line);
  padding: 0 1.15rem;
}

.desktop-mega-menu-group:last-child {
  border-right: 0;
}

.desktop-mega-menu-group h3 {
  margin: 0 0 0.6rem;
  border-bottom: 1px solid var(--mega-line);
  padding: 0 0.45rem 0.58rem;
  color: var(--mega-muted);
  font-size: 0.68rem;
  font-weight: 750;
  line-height: 1.3;
  text-transform: uppercase;
}

.desktop-mega-menu-links {
  display: grid;
  gap: 0.08rem;
}

.desktop-mega-menu-links a {
  display: block;
  min-width: 0;
  border-radius: 0.35rem;
  padding: 0.58rem 0.45rem 0.62rem;
  color: var(--mega-text);
  text-decoration: none;
  transition:
    background 150ms ease,
    color 150ms ease;
}

.desktop-mega-menu-links strong,
.desktop-mega-menu-links small {
  display: block;
  min-width: 0;
}

.desktop-mega-menu-links strong {
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.35;
}

.desktop-mega-menu-links small {
  margin-top: 0.18rem;
  color: var(--mega-muted);
  font-size: 0.65rem;
  line-height: 1.45;
}

.desktop-mega-menu-links a:hover,
.desktop-mega-menu-links a:focus-visible {
  background: var(--mega-hover);
  color: #0e8eb3;
  outline: none;
}

.desktop-mega-menu-dark .desktop-mega-menu-links a:hover,
.desktop-mega-menu-dark .desktop-mega-menu-links a:focus-visible {
  color: #67e8f9;
}

.desktop-mega-menu-enter-active,
.desktop-mega-menu-leave-active {
  transform-origin: top center;
  transition:
    opacity 160ms ease,
    transform 210ms cubic-bezier(0.22, 1, 0.36, 1),
    clip-path 210ms cubic-bezier(0.22, 1, 0.36, 1);
}

.desktop-mega-menu-enter-from,
.desktop-mega-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.45rem);
  clip-path: inset(0 0 100% 0);
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .desktop-brand {
    width: 2.5rem;
  }

  .desktop-navigation-list {
    padding-inline: 0.15rem;
  }

  .desktop-navigation-trigger {
    padding-inline: 0.45rem;
    font-size: 0.72rem;
  }

  .desktop-actions :deep(.language-trigger-desktop span) {
    display: none;
  }

  .desktop-actions :deep(.language-trigger-desktop) {
    width: 2.5rem;
    padding-inline: 0;
  }

  .desktop-action-button {
    min-width: 5.15rem;
    min-height: 2.35rem;
    padding-inline: 0.55rem;
    font-size: 0.72rem;
  }

  .desktop-mega-menu-shell {
    grid-template-columns: minmax(12.5rem, 0.72fr) minmax(0, 2.5fr);
  }

  .desktop-mega-menu-intro {
    padding-inline: 1.25rem;
  }

  .desktop-mega-menu-groups {
    padding-inline: 0.65rem;
  }

  .desktop-mega-menu-group {
    padding-inline: 0.65rem;
  }
}

/* Mobile menu styles */
.hamburger {
  @apply p-2;
  position: relative;
}

/* 40px box, 44px tap area: the extra 2px sits in the gap beside it, so nothing moves. */
.hamburger::before {
  content: "";
  position: absolute;
  inset: -2px;
}

/* The 38px logo link gets the same invisible ring: a 44px tap area, nothing moves. */
#logo {
  position: relative;
}

#logo::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 44px;
  height: 44px;
  transform: translate(-3px, -50%);
}

.hamburger:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 4px;
  border-radius: 0.55rem;
}

.mobile-nav-panel {
  position: fixed;
  inset: 6.25rem 0 0;
  z-index: 20;
  height: calc(100dvh - 6.25rem);
  overflow: hidden auto;
  overscroll-behavior: contain;
  border-top: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-page);
  color: var(--mobile-nav-text);
  box-shadow: 0 28px 72px var(--mobile-nav-shadow);
  outline: none;
}

.mobile-nav-panel-dark {
  --mobile-nav-page: #020617;
  --mobile-nav-line: rgba(148, 163, 184, 0.2);
  --mobile-nav-soft-line: rgba(148, 163, 184, 0.13);
  --mobile-nav-hover: #0b1729;
  --mobile-nav-active: #0c2136;
  --mobile-nav-text: #f8fafc;
  --mobile-nav-shadow: rgba(2, 6, 23, 0.52);
}

.mobile-nav-panel-light {
  --mobile-nav-page: #ffffff;
  --mobile-nav-line: rgba(15, 23, 42, 0.13);
  --mobile-nav-soft-line: rgba(15, 23, 42, 0.08);
  --mobile-nav-hover: #f1f5f9;
  --mobile-nav-active: #ecfeff;
  --mobile-nav-text: #020617;
  --mobile-nav-shadow: rgba(15, 23, 42, 0.12);
}

.mobile-nav-shell {
  width: 100%;
  max-width: 58rem;
  min-height: 100%;
  margin: 0 auto;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition:
    opacity 220ms ease,
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    clip-path 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-0.75rem);
  clip-path: inset(0 0 100% 0);
}

.mobile-nav-enter-to,
.mobile-nav-leave-from {
  opacity: 1;
  transform: translateY(0);
  clip-path: inset(0 0 0 0);
}

.mobile-nav-direct-list {
  display: grid;
  border-top: 1px solid var(--mobile-nav-line);
  border-bottom: 1px solid var(--mobile-nav-line);
}

.mobile-nav-direct-link {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3.25rem;
  border-left: 2px solid transparent;
  border-bottom: 1px solid var(--mobile-nav-soft-line);
  color: var(--mobile-nav-text);
  padding: 0 0.7rem;
  font-size: 0.9rem;
  font-weight: 750;
  text-decoration: none;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease;
}

.mobile-nav-direct-link:last-child {
  border-bottom: 0;
}

.mobile-nav-direct-link span:last-child {
  color: #1ba7ce;
  transition: transform 150ms ease;
}

.mobile-nav-direct-link:hover,
.mobile-nav-direct-link-active {
  border-left-color: #1ba7ce;
  background: var(--mobile-nav-active);
}

.mobile-nav-direct-link:hover span:last-child {
  transform: translateX(0.16rem);
}

.mobile-nav-resource-trigger {
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.mobile-nav-resource-trigger svg {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: #1ba7ce;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  transition: transform 180ms ease;
}

.mobile-nav-resource-trigger-open svg {
  transform: rotate(180deg);
}

.mobile-nav-resource-menu {
  display: grid;
  border-bottom: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-hover);
  padding: 0.25rem 0.7rem 0.45rem;
}

.mobile-nav-resource-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2.65rem;
  border-bottom: 1px solid var(--mobile-nav-soft-line);
  color: var(--mobile-nav-text);
  padding: 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 650;
  text-decoration: none;
  transition:
    color 150ms ease,
    background 150ms ease;
}

.mobile-nav-resource-link:last-child {
  border-bottom: 0;
}

.mobile-nav-resource-link span:last-child {
  color: #1ba7ce;
}

.mobile-nav-resource-link:hover,
.mobile-nav-resource-link-active {
  background: var(--mobile-nav-active);
  color: #1ba7ce;
}

.mobile-resource-menu-enter-active,
.mobile-resource-menu-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.mobile-resource-menu-enter-from,
.mobile-resource-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.35rem);
}

.mobile-nav-actions {
  position: sticky;
  bottom: -1rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  margin: 0 -0.15rem -1rem;
  border-top: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-page);
  padding: 0.78rem 0.15rem calc(1rem + env(safe-area-inset-bottom));
}

/* The terms take their own row under both buttons. TrialDisclosure has two root nodes, so it never gets this
   component's scoped attribute: reach it with :deep or the rule never matches. */
.mobile-nav-actions :deep(.mobile-nav-terms) {
  grid-column: 1 / -1;
  margin: 0;
  max-width: none;
  text-align: center;
}

.mobile-nav-cta,
.mobile-nav-contact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.65rem;
  /* Long labels wrap (fr "Contacter le service commercial" takes 3 lines at 125% text): keep them off the border. */
  padding-block: 0.45rem;
  border-radius: 0.45rem;
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.15;
  text-align: center;
  text-decoration: none;
}

.mobile-nav-cta {
  gap: 0.45rem;
  background: #0e7490;
  color: #ffffff;
  padding-inline: 1rem;
  box-shadow: 0 12px 28px rgba(27, 167, 206, 0.2);
}

.mobile-nav-cta:hover {
  background: #155e75;
  transform: translateY(-1px);
}

.mobile-nav-contact {
  border: 1px solid var(--mobile-nav-line);
  background: transparent;
  color: var(--mobile-nav-text);
  padding-inline: 0.8rem;
}

.mobile-nav-contact:hover {
  border-color: rgba(27, 167, 206, 0.56);
  background: var(--mobile-nav-hover);
}

.mobile-nav-direct-link:focus-visible,
.mobile-nav-resource-link:focus-visible,
.mobile-nav-cta:focus-visible,
.mobile-nav-contact:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

@media (min-width: 640px) {
  .mobile-nav-shell {
    padding: 1.15rem 1.5rem calc(1.15rem + env(safe-area-inset-bottom));
  }

  .mobile-nav-direct-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mobile-nav-direct-link:nth-child(odd) {
    border-right: 1px solid var(--mobile-nav-soft-line);
  }

  .mobile-nav-resource-trigger,
  .mobile-nav-resource-menu,
  .mobile-nav-direct-signin {
    grid-column: 1 / -1;
    border-right: 0;
  }

  .mobile-nav-resource-menu {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 0.7rem;
  }

  .mobile-nav-resource-link:nth-child(odd) {
    border-right: 1px solid var(--mobile-nav-soft-line);
  }

  .mobile-nav-resource-link:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  .mobile-nav-actions {
    grid-template-columns: repeat(2, minmax(0, 11rem));
    justify-content: end;
  }

  /* Buttons sit at the right from 640px, so the terms line up under them. */
  .mobile-nav-actions :deep(.mobile-nav-terms) {
    text-align: right;
  }
}

@media (min-width: 768px) {
  .mobile-nav-panel {
    inset: 7.25rem 0 0;
    height: calc(100dvh - 7.25rem);
  }
}

@media (min-width: 1024px) {
  .desktop-page-backdrop {
    display: block;
  }

  .mobile-nav-shell {
    max-width: 62rem;
    padding-inline: 2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .desktop-navigation-trigger,
  .desktop-navigation-trigger::after,
  .desktop-navigation-trigger svg,
  .desktop-action-button,
  .desktop-page-backdrop-enter-active,
  .desktop-page-backdrop-leave-active,
  .desktop-mega-menu-enter-active,
  .desktop-mega-menu-leave-active,
  .desktop-mega-menu-intro > a span:last-child,
  .mobile-nav-enter-active,
  .mobile-nav-leave-active,
  .mobile-resource-menu-enter-active,
  .mobile-resource-menu-leave-active,
  .mobile-nav-resource-trigger svg,
  .mobile-nav-cta,
  .mobile-nav-contact {
    transition-duration: 1ms;
  }
}
</style>
